import React, { useState } from 'react'
import { Pagination } from '@material-ui/lab'
import { TableSortLabel, useTheme } from '@material-ui/core'
import { Container } from '../../../components'
import {
    InspectionDetailGrades,
    InspectionTypeGrades,
} from '../../../hooks/useAnalyticData'
import { usePagination } from '../../../hooks'
import { Range } from 'react-date-range'
import { DateFilter } from '../../../components/DateFilterButtons'
import { InspectionDetailGradeRow } from './InspectionDetailGradeRow'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    timeline: Range
    dateFilter: DateFilter
    inspectionDetailGrades: InspectionDetailGrades[] | null
    selectedInspectionType: InspectionTypeGrades | null
    setInspectionDetailGrades: (
        inspectionDetailGrades: InspectionDetailGrades[],
    ) => void
    setSelectedInspectionType: (
        inspectionTypeGrades: InspectionTypeGrades,
    ) => void
}

export const InspectionDetailGradeTable = (props: Props) => {
    const { inspectionDetailGrades, setInspectionDetailGrades } = props

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        inspectionDetailGrades ?? [],
    )

    const [orderBy, setOrderBy] = useState('')

    const theme = useTheme()
    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body1),
        fontWeight: 600,
        color: theme.palette.darkGreen.main,
        whiteSpace: 'pre-line',
        justifyContent: 'right',
        flex: 1,
    }

    return (
        <Container
            direction="column"
            style={{ marginTop: theme.spacing(3), flex: 1 }}
        >
            {/* HEADERS */}
            <Container
                style={{
                    padding: theme.spacing(1, 1),
                    borderBottom: '1px solid',
                    borderColor: theme.palette.grey[400],
                }}
            >
                <Container
                    style={{
                        ...headCellStyle,
                    }}
                    onClick={() => {
                        if (inspectionDetailGrades) {
                            const newRows = [...inspectionDetailGrades]
                            if (orderBy !== NameASC) {
                                newRows.sort((RowA, RowB) =>
                                    RowA.name.localeCompare(RowB.name),
                                )
                                setInspectionDetailGrades(newRows)
                                setOrderBy(NameASC)
                            } else {
                                setInspectionDetailGrades(newRows.reverse())
                                setOrderBy(NameDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={orderBy === NameASC || orderBy === NameDESC}
                        direction={orderBy === NameASC ? 'asc' : 'desc'}
                    >
                        Inspection <br /> Name
                    </TableSortLabel>
                </Container>
                <Container
                    style={{
                        ...headCellStyle,
                        minWidth: theme.spacing(20),
                        maxWidth: theme.spacing(20),
                    }}
                    onClick={() => {
                        if (inspectionDetailGrades) {
                            const newRows = [...inspectionDetailGrades]
                            if (orderBy !== EndDateASC) {
                                newRows.sort((RowA, RowB) =>
                                    RowA.end_date.localeCompare(RowB.end_date),
                                )
                                setInspectionDetailGrades(newRows)
                                setOrderBy(EndDateASC)
                            } else {
                                setInspectionDetailGrades(newRows.reverse())
                                setOrderBy(EndDateDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === EndDateASC || orderBy === EndDateDESC
                        }
                        direction={orderBy === EndDateASC ? 'asc' : 'desc'}
                    >
                        End <br /> Date
                    </TableSortLabel>
                </Container>
                <Container
                    style={{ ...headCellStyle }}
                    onClick={() => {
                        if (inspectionDetailGrades) {
                            const newRows = [...inspectionDetailGrades]
                            if (orderBy !== FlagsASC) {
                                newRows.sort(
                                    (RowA, RowB) => RowA.flags - RowB.flags,
                                )
                                setInspectionDetailGrades(newRows)
                                setOrderBy(FlagsASC)
                            } else {
                                setInspectionDetailGrades(newRows.reverse())
                                setOrderBy(FlagsDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={orderBy === FlagsASC || orderBy === NameDESC}
                        direction={orderBy === FlagsASC ? 'asc' : 'desc'}
                    >
                        Flags
                    </TableSortLabel>
                </Container>
                <Container
                    style={{ ...headCellStyle }}
                    onClick={() => {
                        if (inspectionDetailGrades) {
                            const newRows = [...inspectionDetailGrades]
                            if (orderBy !== PotentialFlagsASC) {
                                newRows.sort(
                                    (RowA, RowB) =>
                                        RowA.potential_flags -
                                        RowB.potential_flags,
                                )
                                setInspectionDetailGrades(newRows)
                                setOrderBy(PotentialFlagsASC)
                            } else {
                                setInspectionDetailGrades(newRows.reverse())
                                setOrderBy(PotentialFlagsDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === PotentialFlagsASC ||
                            orderBy === PotentialFlagsDESC
                        }
                        direction={
                            orderBy === PotentialFlagsASC ? 'asc' : 'desc'
                        }
                    >
                        Potential <br /> Flags
                    </TableSortLabel>
                </Container>
                <Container
                    style={{ ...headCellStyle }}
                    onClick={() => {
                        if (inspectionDetailGrades) {
                            const newRows = [...inspectionDetailGrades]
                            if (orderBy !== UnitsInsASC) {
                                newRows.sort(
                                    (RowA, RowB) =>
                                        RowA.units_inspected -
                                        RowB.units_inspected,
                                )
                                setInspectionDetailGrades(newRows)
                                setOrderBy(UnitsInsASC)
                            } else {
                                setInspectionDetailGrades(newRows.reverse())
                                setOrderBy(UnitsInsDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === UnitsInsASC || orderBy === UnitsInsDESC
                        }
                        direction={orderBy === UnitsInsASC ? 'asc' : 'desc'}
                    >
                        Units <br /> Inspected
                    </TableSortLabel>
                </Container>
                <Container
                    style={{ ...headCellStyle }}
                    onClick={() => {
                        if (inspectionDetailGrades) {
                            const newRows = [...inspectionDetailGrades]
                            if (orderBy !== UnitsReqInsASC) {
                                newRows.sort(
                                    (RowA, RowB) =>
                                        RowA.units_requiring_inspection -
                                        RowB.units_requiring_inspection,
                                )
                                setInspectionDetailGrades(newRows)
                                setOrderBy(UnitsReqInsASC)
                            } else {
                                setInspectionDetailGrades(newRows.reverse())
                                setOrderBy(UnitsReqInsDESC)
                            }
                        }
                    }}
                >
                    <TableSortLabel
                        active={
                            orderBy === UnitsReqInsASC ||
                            orderBy === UnitsReqInsDESC
                        }
                        direction={orderBy === UnitsReqInsASC ? 'asc' : 'desc'}
                    >
                        Units <br /> Requiring <br /> Inspection
                    </TableSortLabel>
                </Container>
            </Container>
            {/* body */}
            <Container
                style={{
                    overflowY: 'auto',
                    maxHeight: 'calc(100vh - 350px)',
                    minHeight: 'calc(100vh - 350px)',
                }}
                direction="column"
            >
                {pageData.map((inspectionDetailGrade, idx) => {
                    return (
                        <InspectionDetailGradeRow
                            inspectionDetailGrade={inspectionDetailGrade}
                            key={`INSPECTION_Name_GRADE_${inspectionDetailGrade.id}`}
                        />
                    )
                })}
            </Container>
            {/* footer */}
            <Container
                style={{
                    ...typographyStyle(theme.typography.subtitle1),
                    borderTop: `1px solid ${theme.palette.grey[400]}`,
                    padding: theme.spacing(1),
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Pagination
                    count={numPages}
                    page={page}
                    onChange={(_: unknown, newPage: number) => {
                        setPage(newPage)
                    }}
                />
                <span>
                    {start} - {end} of {inspectionDetailGrades?.length ?? 0}
                </span>
            </Container>
        </Container>
    )
}

const NameASC = 'NameASC'
const NameDESC = 'NameDESC'
const FlagsASC = 'FlagsASC'
const FlagsDESC = 'FlagsDESC'
const PotentialFlagsASC = 'PotentialFlagsASC'
const PotentialFlagsDESC = 'PotentialFlagsDESC'
const UnitsInsASC = 'UnitsInsASC'
const UnitsInsDESC = 'UnitInsDESC'
const UnitsReqInsASC = 'UnitsReqInsASC'
const UnitsReqInsDESC = 'UnitReqInsDESC'
const EndDateASC = 'EndDateASC'
const EndDateDESC = 'EndDateDESC'
